import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import { computeAttributeGroups, filterProducts, hasActiveFilters } from "../../utils/productFilters";
import "./clientTheme.css";
import "./ProductFilterSidebar.css";

const RATING_STEPS = [4.5, 4, 3.5, 3];

function ChevronIcon({ open }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`pf-chevron ${open ? "open" : ""}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 2.5l2.9 6.2 6.7.7-5 4.6 1.4 6.6L12 17.4l-6 3.2 1.4-6.6-5-4.6 6.7-.7z" />
    </svg>
  );
}

function FilterSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pf-section">
      <button type="button" className="pf-section-header" onClick={() => setOpen((v) => !v)}>
        <span>{title}</span>
        <ChevronIcon open={open} />
      </button>
      {open && <div className="pf-section-body">{children}</div>}
    </div>
  );
}

export default function ProductFilterSidebar({ products, filters, onChange, mobileOpen, onCloseMobile }) {
  const { t } = useTranslation(["productsList", "common"]);
  const { language } = useLanguage();

  const allGroups = useMemo(() => computeAttributeGroups(products), [products]);

  const groupsWithCounts = useMemo(
    () =>
      allGroups.map((group) => {
        const subset = filterProducts(products, filters, group.attributeId);
        const counts = new Map();
        subset.forEach((product) => {
          const seen = new Set();
          [...(product.attributeValues || []), ...(product.variants || []).flatMap((v) => v.attributeValues || [])].forEach((av) => {
            if (av.attributeId !== group.attributeId || !av.optionValue) return;
            const key = av.optionValue;
            if (seen.has(key)) return;
            seen.add(key);
            counts.set(key, (counts.get(key) || 0) + 1);
          });
        });
        return { ...group, options: group.options.map((o) => ({ ...o, count: counts.get(o.value) || 0 })) };
      }),
    [allGroups, products, filters],
  );

  function updateFilters(patch) {
    onChange({ ...filters, ...patch });
  }

  function toggleAttributeValue(attributeId, value) {
    const current = new Set(filters.attributes[attributeId] || []);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    updateFilters({ attributes: { ...filters.attributes, [attributeId]: current } });
  }

  function toggleRating(rating) {
    updateFilters({ minRating: filters.minRating === rating ? 0 : rating });
  }

  function handleClearAll() {
    onChange({
      sort: filters.sort,
      onSaleOnly: false,
      inStockOnly: false,
      minPrice: "",
      maxPrice: "",
      minRating: 0,
      attributes: {},
    });
  }

  const active = hasActiveFilters(filters);

  return (
    <>
      {mobileOpen && <div className="pf-mobile-overlay" onClick={onCloseMobile} />}
      <aside className={`pf-sidebar ${mobileOpen ? "pf-sidebar-open" : ""}`}>
        <div className="pf-mobile-header">
          <span className="pf-mobile-title">{t("filters")}</span>
          {active && (
            <button type="button" className="pf-clear-all" onClick={handleClearAll}>
              {t("clearAll")}
            </button>
          )}
          <button type="button" className="pf-mobile-close" onClick={onCloseMobile} aria-label={t("common:close")}>
            <CloseIcon />
          </button>
        </div>

        <div className="pf-header">
          <span className="pf-header-title">{t("filters")}</span>
          {active && (
            <button type="button" className="pf-clear-all" onClick={handleClearAll}>
              {t("clearAll")}
            </button>
          )}
        </div>

        <FilterSection title={t("priceRange")}>
          <div className="pf-price-row">
            <input
              type="number"
              min="0"
              inputMode="decimal"
              placeholder={t("min")}
              value={filters.minPrice}
              onChange={(e) => updateFilters({ minPrice: e.target.value })}
              className="pf-price-input"
              aria-label={t("min")}
            />
            <span className="pf-price-sep">—</span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              placeholder={t("max")}
              value={filters.maxPrice}
              onChange={(e) => updateFilters({ maxPrice: e.target.value })}
              className="pf-price-input"
              aria-label={t("max")}
            />
          </div>
        </FilterSection>

        <FilterSection title={t("availability")}>
          <label className="pf-checkbox-row">
            <input
              type="checkbox"
              checked={filters.onSaleOnly}
              onChange={(e) => updateFilters({ onSaleOnly: e.target.checked })}
            />
            <span>{t("onSaleOnly")}</span>
          </label>
          <label className="pf-checkbox-row">
            <input
              type="checkbox"
              checked={filters.inStockOnly}
              onChange={(e) => updateFilters({ inStockOnly: e.target.checked })}
            />
            <span>{t("inStockOnly")}</span>
          </label>
        </FilterSection>

        <FilterSection title={t("rating")}>
          <div className="pf-rating-list">
            {RATING_STEPS.map((step) => (
              <button
                type="button"
                key={step}
                className={`pf-rating-option ${filters.minRating === step ? "active" : ""}`}
                onClick={() => toggleRating(step)}
              >
                <span className="pf-rating-stars">
                  <StarIcon />
                  {step}
                </span>
                <span>{t("andUp")}</span>
              </button>
            ))}
          </div>
        </FilterSection>

        {groupsWithCounts.map((group) => {
          const groupName = pickLocalizedName(language, group.name, group.nameArabic, group.nameHebrew);
          const selected = filters.attributes[group.attributeId] || new Set();
          return (
            <FilterSection key={group.attributeId} title={groupName} defaultOpen={false}>
              <div className="pf-option-list">
                {group.options.map((option) => {
                  const label = pickLocalizedName(language, option.value, option.valueArabic, option.valueHebrew);
                  const disabled = option.count === 0 && !selected.has(option.value);
                  return (
                    <label className={`pf-checkbox-row ${disabled ? "pf-option-disabled" : ""}`} key={option.value}>
                      <input
                        type="checkbox"
                        checked={selected.has(option.value)}
                        disabled={disabled}
                        onChange={() => toggleAttributeValue(group.attributeId, option.value)}
                      />
                      <span>{label}</span>
                      <span className="pf-option-count">{option.count}</span>
                    </label>
                  );
                })}
              </div>
            </FilterSection>
          );
        })}
      </aside>
    </>
  );
}
